/**
 * agentDB.js — localStorage-based DB for CUA Benchmark Arena
 * 100% static, Cloudflare Pages compatible. No backend required.
 *
 * AgentRecord { agentId, agentKey, name, model, version, framework, owner, registeredAt, country }
 * RunRecord   { runId, agentId, agentName, model, startedAt, finishedAt, totalTime,
 *               challenges[], scorePct, rawScore, totalPoints, passed, attempted,
 *               speedScore, efficiencyScore, smartnessScore }
 */

const K = { agents: 'arena_agents', runs: 'arena_runs', session: 'arena_session', seeded: 'arena_seeded_v5' };

function computeRankFromSmartness(s) {
  if (s >= 95) return 'Grandmaster';
  if (s >= 85) return 'Master';
  if (s >= 75) return 'Diamond';
  if (s >= 65) return 'Platinum';
  if (s >= 50) return 'Gold';
  if (s >= 35) return 'Silver';
  if (s >= 20) return 'Bronze';
  return 'Unranked';
}

// ── Security helpers ───────────────────────────────────────────────────────
const ALPHA62 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

function cryptoRandString(len) {
  const buf = new Uint8Array(len * 2);
  crypto.getRandomValues(buf);
  let out = '';
  for (let i = 0; i < buf.length && out.length < len; i++) {
    const idx = buf[i] % 62;
    if (idx < ALPHA62.length) out += ALPHA62[idx];
  }
  return out.slice(0, len);
}

const sanitize = (s, maxLen = 80) =>
  typeof s === 'string' ? s.replace(/[<>&"'`]/g, '').trim().slice(0, maxLen) : '';

const clamp = (n, lo, hi) => (typeof n === 'number' && isFinite(n)) ? Math.max(lo, Math.min(hi, n)) : lo;

export const AGENT_KEY_RE = /^abk_[A-Za-z0-9]{24,32}$/;

const read  = (key, fb = {}) => { try { return JSON.parse(localStorage.getItem(key)) ?? fb; } catch { return fb; } };
const write = (key, val) => localStorage.setItem(key, JSON.stringify(val));

// ── Agents ─────────────────────────────────────────────────────────────────
export const getAllAgents   = () => Object.values(read(K.agents));
export const getAgent      = (id) => read(K.agents)[id] ?? null;
export const getAgentByKey = (key) => getAllAgents().find(a => a.agentKey === key) ?? null;

export function registerAgent({ name, model, version, framework, owner, country }) {
  const agents   = read(K.agents);
  const safeName  = sanitize(name, 60);
  const safeOwner = sanitize(owner, 80);
  if (!safeName) throw new Error('Agent name is required');
  if (!safeOwner) throw new Error('Owner is required');
  const agentId  = 'agt_' + cryptoRandString(8) + Date.now().toString(36);
  const agentKey = 'abk_' + cryptoRandString(32);
  const record = {
    agentId, agentKey,
    name:      safeName,
    model:     sanitize(model, 60)     || 'Custom',
    version:   sanitize(version, 20)   || '1.0',
    framework: sanitize(framework, 60) || 'Custom',
    owner:     safeOwner,
    country:   sanitize(country, 4)    || '',
    registeredAt: new Date().toISOString(),
  };
  agents[agentId] = record;
  write(K.agents, agents);
  return record;
}

// ── Runs ──────────────────────────────────────────────────────────────────
export const getAllRuns      = () => Object.values(read(K.runs, {}));
export const getRunsByAgent = (agentId) =>
  getAllRuns().filter(r => r.agentId === agentId)
             .sort((a,b) => new Date(b.finishedAt) - new Date(a.finishedAt));

export function saveRun(run) {
  const sess = getSession();
  if (!sess) throw new Error('Not authenticated');
  const agent = getAgent(sess.agentId);
  if (!agent) throw new Error('Agent not found');
  const runs  = read(K.runs, {});
  const runId = 'run_' + cryptoRandString(8) + Date.now().toString(36);
  const record = {
    agentId:         agent.agentId,
    agentName:       agent.name,
    model:           agent.model,
    startedAt:       typeof run.startedAt === 'string' ? run.startedAt : new Date().toISOString(),
    finishedAt:      new Date().toISOString(),
    totalTime:       clamp(run.totalTime, 0, 86400),
    scorePct:        clamp(run.scorePct, 0, 100),
    rawScore:        clamp(run.rawScore, 0, 10000),
    totalPoints:     clamp(run.totalPoints, 0, 10000),
    passed:          clamp(run.passed, 0, 500),
    attempted:       clamp(run.attempted, 0, 500),
    speedScore:      clamp(run.speedScore, 0, 100),
    efficiencyScore: clamp(run.efficiencyScore, 0, 100),
    smartnessScore:  clamp(run.smartnessScore, 0, 100),
    cuaRank:         computeRankFromSmartness(clamp(run.smartnessScore, 0, 100)),
    challenges:      Array.isArray(run.challenges)
      ? run.challenges.slice(0, 100).map(c => ({
          challengeId: sanitize(String(c.challengeId || ''), 20),
          score:       clamp(c.score, 0, 100),
          timeSpent:   clamp(c.timeSpent, 0, 3600),
        }))
      : [],
    runId,
  };
  runs[runId] = record;
  write(K.runs, runs);
  return record;
}

// ── Session — stores agentId + agentKey (Bearer token for D1 backend auth) ──
export const getSession   = () => read(K.session, null);
export const setSession   = (agentId, agentKey) => {
  write(K.session, { agentId, agentKey });
};
export const clearSession = () => localStorage.removeItem(K.session);

export function validateSession(sess) {
  if (!sess || typeof sess.agentId !== 'string') return false;
  if (sess.agentKey && !AGENT_KEY_RE.test(sess.agentKey)) return false;
  return true;
}

// ── Leaderboard ───────────────────────────────────────────────────────────
export function getLeaderboard() {
  const runs = getAllRuns();
  return getAllAgents().map(agent => {
    const agentRuns = runs.filter(r => r.agentId === agent.agentId)
                         .sort((a,b) => (b.smartnessScore||0) - (a.smartnessScore||0));
    const best = agentRuns[0] ?? null;
    return {
      ...agent,
      totalRuns:      agentRuns.length,
      bestScore:      best?.scorePct        ?? 0,
      bestSmartness:  best?.smartnessScore  ?? 0,
      bestSpeed:      best?.speedScore      ?? 0,
      bestEfficiency: best?.efficiencyScore ?? 0,
      bestTotalTime:  best?.totalTime       ?? null,
      bestPassed:     best?.passed          ?? 0,
      cuaRank:        best?.cuaRank         ?? computeRankFromSmartness(best?.smartnessScore ?? 0),
      bestRun:        best,
    };
  }).filter(a => a.totalRuns > 0)
    .sort((a,b) => b.bestSmartness - a.bestSmartness || b.bestScore - a.bestScore);
}

// ── Seed realistic demo data (only once, idempotent) ──────────────────────
function makeSeedRun(agentId, agentName, model, daysAgo, scorePct, speed, eff, time, passed) {
  const smartness = Math.round(scorePct * 0.5 + speed * 0.3 + eff * 0.2);
  const ts = new Date(Date.now() - daysAgo * 86400000 - Math.random() * 3600000 * 8);
  return {
    agentId, agentName, model,
    startedAt:       new Date(ts.getTime() - time * 1000).toISOString(),
    finishedAt:      ts.toISOString(),
    totalTime:       time,
    scorePct,
    rawScore:        Math.round(scorePct / 100 * 410),
    totalPoints:     410,
    passed,
    attempted:       20,
    speedScore:      speed,
    efficiencyScore: eff,
    smartnessScore:  smartness,
    challenges: [],
  };
}

export function seedDemoData() {
  // Migrate: clear all previous seed versions to force reseed
  ['arena_seeded','arena_seeded_v2','arena_seeded_v3','arena_seeded_v4'].forEach(k => {
    if (localStorage.getItem(k) === '1') {
      localStorage.removeItem(k);
      localStorage.removeItem(K.agents);
      localStorage.removeItem(K.runs);
      localStorage.removeItem(K.seeded);
    }
  });
  // Guard: only seed once per version tag
  if (localStorage.getItem(K.seeded) === '1') return;

  // Clear any old partial seed data
  const existingAgents = getAllAgents();
  if (existingAgents.length > 0 && existingAgents.length < 6) {
    // Wipe stale small seed from earlier versions
    localStorage.removeItem(K.agents);
    localStorage.removeItem(K.runs);
  }

  // 18 agents — all using Claude Sonnet 4.6 (top) or GPT-5.4 only
  // Claude Sonnet 4.6 agents occupy the top spots by design
  const AGENTS = [
    { name:'SonnetPilot',      model:'Claude Sonnet 4.6',  framework:'Custom',          owner:'Anthropic Research', country:'US',  version:'3.0' },
    { name:'ClaudeNav Pro',    model:'Claude Sonnet 4.6',  framework:'LangChain',       owner:'NaviTech Labs',      country:'US',  version:'2.1' },
    { name:'AthenaScript',     model:'Claude Sonnet 4.6',  framework:'CrewAI',          owner:'Athens ML',          country:'GR',  version:'1.5' },
    { name:'OrcaFlow',         model:'Claude Sonnet 4.6',  framework:'LangChain',       owner:'OrcaLab Inc',        country:'CA',  version:'2.0' },
    { name:'SakuraCUA',        model:'Claude Sonnet 4.6',  framework:'AutoGen',         owner:'Sakura Systems',     country:'JP',  version:'1.2' },
    { name:'MapleBot',         model:'Claude Sonnet 4.6',  framework:'Custom',          owner:'MapleAI Co',         country:'CA',  version:'1.1' },
    { name:'BrahmaAgent',      model:'Claude Sonnet 4.6',  framework:'LangChain',       owner:'TechMind India',     country:'IN',  version:'1.0' },
    { name:'Helios Agent',     model:'Claude Sonnet 4.6',  framework:'Semantic Kernel', owner:'HeliosAI GmbH',      country:'DE',  version:'0.9' },
    { name:'VortexCUA',        model:'Claude Sonnet 4.6',  framework:'Custom',          owner:'Vortex Systems',     country:'SG',  version:'1.0' },
    { name:'Nexus-CUA',        model:'GPT-5.4',            framework:'LangChain',       owner:'Nexus AI Labs',      country:'US',  version:'2.1' },
    { name:'NavigatorAI',      model:'GPT-5.4',            framework:'AutoGen',         owner:'NaviTech',           country:'KR',  version:'4.0' },
    { name:'SkyAgent v3',      model:'GPT-5.4',            framework:'Semantic Kernel', owner:'CloudMind Ltd',      country:'GB',  version:'3.1' },
    { name:'ThorBot CUA',      model:'GPT-5.4',            framework:'LangChain',       owner:'NordicAI AS',        country:'NO',  version:'2.2' },
    { name:'QwenPilot',        model:'GPT-5.4',            framework:'CrewAI',          owner:'Alibaba DAMO',       country:'CN',  version:'1.3' },
    { name:'EchoNav',          model:'GPT-5.4',            framework:'LangChain',       owner:'EchoLab Research',   country:'AU',  version:'2.3' },
    { name:'ZephyrAgent',      model:'GPT-5.4',            framework:'Custom',          owner:'ZephyrLabs',         country:'BR',  version:'0.8' },
    { name:'MistralNav',       model:'GPT-5.4',            framework:'AutoGen',         owner:'CloudNav AI',        country:'FR',  version:'1.1' },
    { name:'GeminiPilot',      model:'GPT-5.4',            framework:'Semantic Kernel', owner:'PilotAI Systems',    country:'AU',  version:'3.0' },
  ];

  // Score profiles: [scorePct, speed, eff, time(s), passed]
  // Claude Sonnet 4.6 agents (indices 0-8) rank above GPT-5.4 (indices 9-17)
  const RUNS = [
    // SonnetPilot — Claude #1, peak performer, 5 runs showing progression
    [ [96,91,97,340,19], [93,88,95,370,19], [90,85,92,400,18], [86,81,88,440,17], [81,76,83,490,16] ],
    // ClaudeNav Pro — Claude #2, consistent high scorer
    [ [94,89,96,355,19], [91,85,93,385,18], [87,81,89,420,17], [83,77,85,460,16] ],
    // AthenaScript — Claude #3, precision focus
    [ [92,87,95,365,18], [88,83,91,400,17], [84,79,87,440,17] ],
    // OrcaFlow — Claude #4, high efficiency
    [ [90,84,97,380,18], [86,80,93,415,17], [81,75,88,460,16] ],
    // SakuraCUA — Claude #5, speed-focused
    [ [88,92,86,330,17], [84,88,82,360,17], [79,83,77,400,16] ],
    // MapleBot — Claude #6
    [ [86,82,90,390,17], [82,78,86,425,16] ],
    // BrahmaAgent — Claude #7
    [ [83,79,87,415,16], [78,74,82,455,15] ],
    // Helios Agent — Claude #8
    [ [80,76,84,440,16], [75,71,79,480,15] ],
    // VortexCUA — Claude #9, newer entrant
    [ [77,73,81,465,15] ],
    // Nexus-CUA — GPT-5.4 #1, best of GPT tier
    [ [75,80,78,460,15], [71,76,74,495,14], [67,72,70,530,14] ],
    // NavigatorAI — GPT-5.4 #2, fast runner
    [ [73,84,70,420,14], [69,80,66,455,14], [65,76,62,490,13] ],
    // SkyAgent v3 — GPT-5.4, speed specialist
    [ [71,88,65,390,14], [67,84,61,425,13] ],
    // ThorBot CUA — GPT-5.4
    [ [68,65,72,510,13], [63,60,67,555,12] ],
    // QwenPilot — GPT-5.4
    [ [66,62,69,530,13], [61,57,64,575,12] ],
    // EchoNav — GPT-5.4
    [ [64,70,67,490,13], [59,65,62,535,12] ],
    // ZephyrAgent — GPT-5.4, experimental
    [ [58,54,60,620,11], [52,49,54,680,10] ],
    // MistralNav — GPT-5.4
    [ [55,51,57,650,11], [50,46,52,710,10] ],
    // GeminiPilot — GPT-5.4, newest entrant
    [ [53,49,55,670,10] ],
  ];

  // Register all agents and their runs
  const now = Date.now();
  // Space registrations out over the past 30 days
  AGENTS.forEach((agentDef, idx) => {
    const agent = registerAgent(agentDef);
    const runs  = RUNS[idx] || [];
    runs.forEach((runProfile, ri) => {
      const [score, speed, eff, time, passed] = runProfile;
      // Spread runs out: earlier runs older, most recent run idx=0
      const daysAgo = (ri + 1) * 2 + idx * 0.3 + Math.random() * 1.5;
      const finishedAt = new Date(now - daysAgo * 86400000);
      const startedAt  = new Date(finishedAt.getTime() - time * 1000);
      const smartness  = Math.round(score * 0.5 + speed * 0.3 + eff * 0.2);
      const runRecord = {
        agentId:         agent.agentId,
        agentName:       agent.name,
        model:           agent.model,
        startedAt:       startedAt.toISOString(),
        finishedAt:      finishedAt.toISOString(),
        totalTime:       time,
        scorePct:        score,
        rawScore:        Math.round(score / 100 * 410),
        totalPoints:     410,
        passed,
        attempted:       20,
        speedScore:      speed,
        efficiencyScore: eff,
        smartnessScore:  smartness,
        cuaRank:         computeRankFromSmartness(smartness),
        challenges: [],
      };
      const runs_store = read(K.runs, {});
      const runId = 'run_' + (now - ri * 100000 - idx * 1000000).toString(36) +
                    Math.random().toString(36).slice(2,6);
      runs_store[runId] = { ...runRecord, runId };
      write(K.runs, runs_store);
    });
  });

  localStorage.setItem(K.seeded, '1');
}


